import { Injectable, inject } from '@angular/core';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { BCAReports, CCEDewPointReports, CCEReports, DLEReportData, MSSTReportData, ParaffinReports, SummaryOfCCEReports, viscosityReports } from '../../models/lab-test';
import { ReportContentTable, ReservoirProperties, ReservoirPropertiesDST, ReservoirPropertiesSeparator, SampleAnalyses, SampleInformation, SampleInformationDetails, SampleInformationDetailsSeparator, SampleInformationLabel, SampleInformationSeparator, SaturatedProperties, WellInformation } from '../../models/shared';
import { RecombinationQCReportData } from '../../recombination-qc/models/recombination-qc';
import { DecontaminationReportData } from '../../liveoil-decontamination/models/decontamination';
import { reportConstants } from '../../common/constants';
import { CVDReport, CVDStageResult } from '../../cvd/models/cvd';
import { SwellingFinalReport } from '../../swell/models/swelling';
import { CommonBodyStyles, CommonBodyStylesOthers, CommonBodyStyleWithBorder, CommonHeadStyles, CommonHeadStylesOthers, CommonStyles, CommonStylesOthers, FractionReports, SeparatorGasReports, SeparatorOilReports } from '../../models/project';
import { FinalReportDetailsModel } from '../../models/sample';
import { ReportGeneralUtilityService } from '../report.generalUtility.service';
import { ReportPDFDiffLibRenderService } from './report.pdfDiffLibRender.service';
import { ReportPDFCvdRenderService } from './report.pdfCvdRender.service';
import { ReportPDFMsstRenderService } from './report.pdfMsstRender.service';
import { ReportPDFViscosityRenderService } from './report.pdfViscosityRender.service';
import { ReportPDFSwellingRenderService } from './report.pdfSwellingRender.service';
import { ReportPDFCceRenderService } from './report.pdfCceRender.service';
import { ReportPDFRecombinationQcRenderService } from './report.pdfRecombinationQcRender.service';
import { ReportPDFDecontaminationRenderService } from './report.pdfDecontaminationRender.service';
import { ReportPDFFractionRenderService } from './report.pdfFractionRender.service';
import { ReportPDFSeparatorGasRenderService } from './report.pdfSeparatorGasRender.service';
import { ReportPDFSeparatorOilRenderService } from './report.pdfSeparatorOilRender.service';
import { ReportPDFParaffinRenderService } from './report.pdfParaffinRender.service';
import { ReportPDFCompositionRenderService } from './report.pdfCompositionRender.service';
@Injectable({
    providedIn: 'root'
})
export class ReportPDFRenderersService {
    private _reportGeneralUtilityService = inject(ReportGeneralUtilityService);
    private reportPDFDiffLibRenderService = inject(ReportPDFDiffLibRenderService);
    private reportPDFCvdRenderService = inject(ReportPDFCvdRenderService);
    private reportPDFMsstRenderService = inject(ReportPDFMsstRenderService);
    private reportPDFViscosityRenderService = inject(ReportPDFViscosityRenderService);
    private reportPDFSwellingRenderService = inject(ReportPDFSwellingRenderService);
    private reportPDFCceRenderService = inject(ReportPDFCceRenderService);
    private reportPDFRecombinationQcRenderService = inject(ReportPDFRecombinationQcRenderService);
    private reportPDFDecontaminationRenderService = inject(ReportPDFDecontaminationRenderService);
    private reportPDFFractionRenderService = inject(ReportPDFFractionRenderService);
    private reportPDFSeparatorGasRenderService = inject(ReportPDFSeparatorGasRenderService);
    private reportPDFSeparatorOilRenderService = inject(ReportPDFSeparatorOilRenderService);
    private reportPDFParaffinRenderService = inject(ReportPDFParaffinRenderService);
    private reportPDFCompositionRenderService = inject(ReportPDFCompositionRenderService);
    /*New PDF Report Start*/

    commonStyles: CommonStyles = {
        fontSize: 8,
        textColor: [0, 0, 0],
        fillColor: false,
        lineColor: [0, 0, 0],
        lineWidth: reportConstants.tableLinewidth
    };

    commonHeadStyles: CommonHeadStyles = {
        fontSize: 10,
        halign: reportConstants.center,
        textColor: [0, 0, 0],
        fillColor: false,
        fontStyle: reportConstants.normal,
        font: reportConstants.eMprintCondensedRegular,
        cellPadding: { top: 0.5, bottom: 0.5 },
        lineWidth: 0
    };

    commonBodyStyles: CommonBodyStyles = {
        fontSize: 8,
        halign: reportConstants.center,
        textColor: [0, 0, 0],
        fillColor: false,
        font: reportConstants.eMprintCondensedRegular,
        cellPadding: { top: 0.5, bottom: 0.5 }
    };

    commonStylesOthers: CommonStylesOthers = {
        fontSize: 10,
        textColor: [0, 0, 0],
        fillColor: false,
        lineColor: [0, 0, 0],
        lineWidth: reportConstants.tableLinewidth
    }

    commonHeadStylesOthers: CommonHeadStylesOthers = {
        fontSize: 12,
        halign: reportConstants.center,
        textColor: [0, 0, 0],
        fillColor: false,
        fontStyle: reportConstants.normal,
        font: reportConstants.eMprintCondensedBold,
        cellPadding: { top: 0.5, bottom: 0.5 },
        lineWidth: 0
    };

    commonBodyStylesOthers: CommonBodyStylesOthers = {
        fontSize: 10,
        halign: reportConstants.center,
        textColor: [0, 0, 0],
        fillColor: false,
        font: reportConstants.eMprintCondensedRegular,
        cellPadding: { top: 0.5, bottom: 0.5 }
    };

    commonStylesWithoutBorder: CommonStyles = {
        fontSize: 8,
        textColor: [0, 0, 0],
        fillColor: false,
        lineColor: [0, 0, 0],
        lineWidth: 0
    };
    commonHeadStylesWithoutBorder: CommonHeadStyles = {
        fontSize: 10,
        halign: reportConstants.center,
        textColor: [0, 0, 0],
        fillColor: false,
        fontStyle: reportConstants.normal,
        font: reportConstants.eMprintCondensedBold,
        cellPadding: { top: 0.5, bottom: 0.5 },
        lineWidth: 0 // Remove border
    };
    commonBodyStylesWithoutBorder: CommonBodyStyleWithBorder = {
        fontSize: 8,
        halign: reportConstants.center,
        textColor: [0, 0, 0],
        fillColor: false,
        font: reportConstants.eMprintCondensedRegular,
        cellPadding: { top: 0.5, bottom: 0.5 },
        lineWidth: 0
    }





    /*New PDF Report End*/

    DiffLibTest(doc: jsPDF, marginSize: number, dleExp: DLEReportData, appendix = "", wellNameVersion: string, sampleNameVersion: string) {
        this.reportPDFDiffLibRenderService.DiffLibTest(doc, marginSize, dleExp, appendix, wellNameVersion, sampleNameVersion);
    }

    DiffLibTestAppendix(doc: jsPDF, marginSize: number, dleExp: DLEReportData) {
        this.reportPDFDiffLibRenderService.DiffLibTestAppendix(doc, marginSize, dleExp);
    }






    /* Pages */

    FrontPage(doc: jsPDF, marginSize: number, wellDetails: string[], frontPageNames: string[], logoDataUrl: string, officeDataUrl: string) {
        const margin = marginSize;
        const pageWidth = doc.internal.pageSize.getWidth();
        const usableWidth = pageWidth - 2 * margin;

        // Logo Image - 50% width, centered
        const logoImageWidth = usableWidth * 0.5;
        const logoImageHeight = 20;
        const logoImageX = margin + (usableWidth - logoImageWidth) / 2;
        let currentY = margin;

        doc.addImage(logoDataUrl, reportConstants.pngCap, logoImageX - 8, currentY, logoImageWidth + 10, logoImageHeight, 'frontPageLogo', 'FAST');

        currentY += logoImageHeight + 10;

        // Text Content
        doc.setFont(reportConstants.eMprintCondensedRegular);
        doc.setFontSize(18);

        this.centerText(doc, reportConstants.companyTagline, currentY); currentY += 15;

        doc.setFont(reportConstants.eMprintCondensedBold);
        doc.setFontSize(18);
        this.centerText(doc, reportConstants.reservoirFluidPVTStudy, currentY); currentY += 7;
        this.centerText(doc, reportConstants.wellbore, currentY); currentY += 7;

        doc.setFont(reportConstants.eMprintCondensedRegular);
        doc.setFontSize(12);

        const namesWell = wellDetails;// [reportConstants.wellbore4, reportConstants.mdtSample, reportConstants.date];
        namesWell.forEach(name => {
            if (name !== undefined && name !== '') {
                this.centerText(doc, name, currentY);
                currentY += 5;
            }
        });

        currentY += 5;
        doc.setFont(reportConstants.eMprintCondensedBold);
        doc.setFontSize(18);
        this.centerText(doc, reportConstants.emhc, currentY);
        currentY += 5;

        doc.setFont(reportConstants.eMprintCondensedRegular);
        doc.setFontSize(10);

        const names = frontPageNames;
        names.forEach(name => {
            this.centerText(doc, name, currentY);
            currentY += 5;
        });

        // Office Image - full width, directly after last text

        const officeImageHeight = 90;
        doc.addImage(officeDataUrl, reportConstants.pngCap, margin, currentY + 5, usableWidth, officeImageHeight, 'frontPageOffice', 'FAST');

    }
    CoverPage(doc: jsPDF, marginSize: number, coverDate: string, addressToName: string, fromName: string, coverContent: string[], dearName: string, finalReportDetails: FinalReportDetailsModel[], coverLogoDataUrl: string) {
        doc.addPage();
        const finalDetails = finalReportDetails.find(x => x);
        const margin = marginSize;
        const pageWidth = doc.internal.pageSize.getWidth();
        const usableWidth = pageWidth - 2 * margin;
        let currentY = margin;

        // Sender Address Block (Left-aligned)
        const senderLines = [finalDetails?.fromAddress1 ?? "", finalDetails?.fromAddress2 ?? "", finalDetails?.fromAddress3 ?? "", finalDetails?.fromAddress4 ?? ""] as string[];
        senderLines.forEach((line, index) => {
            if (index < 2) {
                doc.setFont(reportConstants.eMprintCondensedBold);
                doc.text(line, margin, currentY);
            } else {
                doc.setFont(reportConstants.eMprintCondensedRegular);
                doc.text(line, margin, currentY);
            }
            currentY += 5;
        });

        // Exxon Logo (Right-aligned, placed after address block)
        const logoWidth = 35;
        const logoHeight = 8;
        const logoX = pageWidth - margin - logoWidth;
        const logoY = currentY; // Adjust to align with address block height
        doc.addImage(coverLogoDataUrl, reportConstants.pngCap, logoX - 20, logoY, logoWidth, logoHeight, 'coverLogo', 'FAST');

        // Move down a bit after address block
        currentY += 20;
        doc.setFontSize(12);
        // Date and Recipient Info (Left-aligned)
        doc.setFont(reportConstants.eMprintCondensedRegular);
        doc.text(coverDate, margin, currentY);
        currentY += 15;
        doc.setFont(reportConstants.eMprintCondensedBold);
        doc.text(addressToName, margin, currentY);
        currentY += 5;
        doc.setFont(reportConstants.eMprintCondensedRegular);
        const senderLinesTo = [finalDetails?.toAddress1 ?? "", finalDetails?.toAddress2 ?? "", finalDetails?.toAddress3 ?? "", finalDetails?.toAddress4 ?? ""] as string[];
        senderLinesTo.forEach((line) => {
            doc.setFont(reportConstants.eMprintCondensedRegular);
            doc.text(line, margin, currentY);
            currentY += 5;
        });
        currentY += 15;
        doc.text(`${reportConstants.dear} ${dearName},`, margin, currentY);
        currentY += 10;

        doc.setFontSize(12);

        const bodyLines = coverContent;

        bodyLines.forEach(paragraph => {
            currentY = this.JustifyText(doc, paragraph, margin, currentY, usableWidth, 5);
            currentY += 5; // Extra spacing between paragraphs
        });
        doc.setFontSize(12);
        // Closing
        const splitData = (finalDetails?.by ?? "").split(",");
        currentY += 10;
        const centerX = pageWidth / 2;
        const centerXNew = centerX / 2;
        doc.text(reportConstants.regards, centerX + centerXNew, currentY); currentY += 5;
        doc.text(fromName, centerX + centerXNew, currentY); currentY += 10;
        doc.text("By ________________", margin, currentY);
        currentY += 10;
        for (const item of splitData) {
            doc.text(item ?? "", margin, currentY); currentY += 5;
        }

        const labelWidth = 25;
        doc.text(reportConstants.attachment, margin, currentY);
        const attachmentText = finalDetails?.attachment ?? '';
        const attachmentLines = doc.splitTextToSize(
            attachmentText,
            pageWidth - (margin * 2) - labelWidth
        );
        doc.setFontSize(11);
        doc.text(attachmentLines, margin + labelWidth, currentY);
    }
    TableOfContents(doc: jsPDF, tableItem: ReportContentTable[]) {
        doc.setFontSize(16);
        doc.setFont(reportConstants.eMprintCondensedBold);
        doc.text(reportConstants.tableOfContent, 20, 40);

        const tocItems = tableItem;

        let y = 50;
        doc.setFontSize(12);

        // Table headers
        doc.text('', 20, y);
        doc.text(reportConstants.page, 190, y, { align: reportConstants.right });
        y += 8;

        doc.setFont(reportConstants.eMprintCondensedRegular);
        tocItems.forEach(item => {
            const leftMargin = 20;
            const rightMargin = 190;
            const title = item.title;
            const page = item.page;


            if (page === '') {
                // No dots, just title
                doc.text(title, leftMargin, y);
                y += 8;
            } else {

                const titleWidth = doc.getTextWidth(title);
                const pageWidth = doc.getTextWidth(page);
                const availableWidth = rightMargin - leftMargin;

                // Calculate dots
                const dotsWidth = availableWidth - titleWidth - pageWidth - 4; // 4px padding
                const dot = '.';
                const dotWidth = doc.getTextWidth(dot);
                const dotCount = Math.floor(dotsWidth / dotWidth);
                const dots = dot.repeat(dotCount);

                // Final line
                const line = `${title} ${dots} ${page}`;
                doc.text(line, leftMargin, y);
                y += 8;
            }
        });
    }
    SummaryOfAnalyses(doc: jsPDF, summaryContent: SampleAnalyses[], margin: number) {
        doc.addPage();
        const pageHeight = doc.internal.pageSize.getHeight();
        const pageWidth = doc.internal.pageSize.getWidth();
        const usableWidth = pageWidth - 2 * margin;

        doc.setFontSize(16);
        doc.setFont(reportConstants.eMprintCondensedBold);
        let currentY = margin;

        doc.text(reportConstants.summaryOfAnalysesMethods, margin, currentY);
        currentY += 10;

        summaryContent.forEach((item) => {
            doc.setFont(reportConstants.eMprintCondensedRegular);
            doc.setFontSize(12);

            // Estimate content height
            let contentHeight = 5; // title height
            item.content.forEach(paragraph => {
                const lines = doc.splitTextToSize(paragraph, usableWidth);
                contentHeight += lines.length * 5 + 5;
            });

            // Check if there's enough space for title + content
            if (currentY + contentHeight > pageHeight - margin) {
                doc.addPage();
                currentY = margin;
            }

            // Render title
            doc.setFont(reportConstants.eMprintCondensedBold);
            doc.text(item.title, margin, currentY);
            currentY += 5;


            // Render content
            doc.setFont(reportConstants.eMprintCondensedRegular);
            doc.setFontSize(10);
            item.content.forEach(paragraph => {
                currentY = this.LeftAlignText(doc, paragraph, margin, currentY, usableWidth, 5);
                currentY += 5;
            });
        });
    }
    WellAndSamplingInformation(doc: jsPDF, margin: number, wellInformation: WellInformation, sampleInformation: SampleInformation, sampleInformationSeparator: SampleInformationSeparator, sampleType: string) {
        doc.addPage();
        const wellInfoLabels: Record<keyof WellInformation, string> = reportConstants.wellInfoLabels;
        const sampleInfoLabels: Record<keyof SampleInformationLabel, string> = reportConstants.sampleInformationLabels;
        const sampleInfoSeparatorLabels: Record<keyof SampleInformationSeparator, string> = reportConstants.sampleInformationSeparatorLabels;
        const pageWidth = doc.internal.pageSize.getWidth();
        const usableWidth = pageWidth - 2 * margin;

        doc.setFontSize(16);
        doc.setFont(reportConstants.eMprintCondensedBold);
        let currentY = margin;

        // Section title
        doc.text(wellInformation.well + ' ' + reportConstants.samplingInformation, margin, currentY);
        currentY += 10;

        // Draw gray header bar
        const headerHeight = 7;
        doc.setFillColor(220, 220, 220); // Light gray
        doc.rect(margin, currentY, usableWidth, headerHeight, reportConstants.fChar);
        doc.setFontSize(12);
        doc.setTextColor(0);
        doc.text(reportConstants.reservoirAndWellInformation, margin + 2, currentY + 5);
        currentY += headerHeight + 4;
        currentY += 3;
        // Field-value pairs with dotted leaders
        doc.setFontSize(10);
        doc.setFont(reportConstants.eMprintCondensedRegular);


        (Object.keys(wellInformation) as (keyof WellInformation)[]).forEach(key => {
            const label = wellInfoLabels[key] || key;
            const value = String(wellInformation[key] || '');

            const labelX = margin + 20;
            const valueX = (pageWidth / 2) + (margin / 2) + 40;
            const maxLabelWidth = valueX - labelX - 1; // Space for label + dots

            const labelWidth = doc.getTextWidth(label);
            const dotWidth = doc.getTextWidth('.');
            const dotsCount = Math.floor((maxLabelWidth - labelWidth) / dotWidth);
            const dots = '.'.repeat(dotsCount > 0 ? dotsCount : 0);

            const line = `${label} ${dots}`;
            doc.text(line, labelX, currentY);
            doc.text(value, valueX, currentY);
            currentY += 5;
        });

        // Draw gray header bar
        doc.setFont(reportConstants.eMprintCondensedBold);
        doc.setFillColor(220, 220, 220); // Light gray
        doc.rect(margin, currentY, usableWidth, headerHeight, reportConstants.fChar);
        doc.setFontSize(12);
        doc.setTextColor(0);
        doc.text(reportConstants.samplingInformation, margin + 2, currentY + 5);
        currentY += headerHeight + 4;
        currentY += 3; // Extra space before next section
        doc.setFontSize(10);
        doc.setFont(reportConstants.eMprintCondensedRegular);
        if (sampleType === reportConstants.reportTypeSeparatorTest) {
            (Object.keys(sampleInformationSeparator) as (keyof SampleInformationSeparator)[]).forEach(key => {

                const label = sampleInfoSeparatorLabels[key] || key;
                const value = sampleInformationSeparator[key] || '';

                const labelX = margin + 20;
                const valueX = (pageWidth / 2) + (margin / 2) + 40;
                const maxLabelWidth = valueX - labelX - 1; // Space for label + dots

                const labelWidth = doc.getTextWidth(label);
                const dotWidth = doc.getTextWidth('.');
                const dotsCount = Math.floor((maxLabelWidth - labelWidth) / dotWidth);
                const dots = '.'.repeat(dotsCount > 0 ? dotsCount : 0);

                const line = `${label} ${dots}`;
                doc.text(line, labelX, currentY);
                doc.text(value, valueX, currentY);
                currentY += 5;
            });
        } else {
            (Object.keys(sampleInformation) as (keyof SampleInformationLabel)[]).forEach(key => {

                const label = sampleInfoLabels[key] || key;
                const value = sampleInformation[key] || '';

                const labelX = margin + 20;
                const valueX = (pageWidth / 2) + (margin / 2) + 40;
                const maxLabelWidth = valueX - labelX - 1; // Space for label + dots

                const labelWidth = doc.getTextWidth(label);
                const dotWidth = doc.getTextWidth('.');
                const dotsCount = Math.floor((maxLabelWidth - labelWidth) / dotWidth);
                const dots = '.'.repeat(dotsCount > 0 ? dotsCount : 0);

                const line = `${label} ${dots}`;
                doc.text(line, labelX, currentY);
                doc.text(value, valueX, currentY);
                currentY += 5;
            });
        }
        if (sampleType === reportConstants.reportTypeDST) {
            doc.text(reportConstants.depthNote, margin + 20, currentY + 10);
        }
    }
    WellAndSamplingDetails(
        doc: jsPDF,
        margin: number,
        sampleInformationDetails: SampleInformationDetails[],
        reservoirProperties: ReservoirProperties[],
        reservoirPropertiesDST: ReservoirPropertiesDST[],
        saturatedProperties: SaturatedProperties[],
        sampleInformationDetailsSeparator: SampleInformationDetailsSeparator[],
        reservoirPropertiesSeparator: ReservoirPropertiesSeparator[],
        wellInformation: WellInformation,
        reportType: string,
    ) {
        margin -= 5;
        doc.addPage();
        let currentY = 40;

        // Sampling Information
        doc.setFontSize(16);
        doc.setFont(reportConstants.eMprintCondensedBold);
        doc.text(`${wellInformation.well} ${reportConstants.samplingInformation}`, margin, currentY);
        currentY += 5;
        if (reportType === reportConstants.reportTypeSeparatorTest) {
            const sampleHead = reportConstants.sampleHeadPDF;
            const sampleBody = sampleInformationDetailsSeparator.map(val => [
                val.sampleId ?? reportConstants.dash,
                val.sampleDepth ?? reportConstants.dash,
                val.sampleType ?? reportConstants.dash,
                val.reservoir ?? reportConstants.dash,
                val.bottleType ?? reportConstants.dash,
                val.sampleVolume ?? reportConstants.dash,
                val.openingTemperature ?? reportConstants.dash,
                val.openingPressure ?? reportConstants.dash
            ]);
            this.GenerateAutoTable(doc, margin, currentY, sampleHead, sampleBody);

            if (doc.lastAutoTable) {
                currentY = doc.lastAutoTable.finalY + 10;
            }

        }
        else {
            const sampleHead = reportConstants.sampleHeadPDFExtended;
            const sampleBody = sampleInformationDetails.map(val => [
                val.sampleId ?? reportConstants.dash,
                val.sampleDepth ?? reportConstants.dash,
                val.sampleType ?? reportConstants.dash,
                val.reservoir ?? reportConstants.dash,
                val.bottleType ?? reportConstants.dash,
                val.sampleVolume ?? reportConstants.dash,
                val.bottleSN ?? reportConstants.dash,
                val.closingTemperature ?? reportConstants.dash,
                val.closingPressure ?? reportConstants.dash,
                val.openingTemperature ?? reportConstants.dash,
                val.openingPressure ?? reportConstants.dash
            ]);
            this.GenerateAutoTable(doc, margin, currentY, sampleHead, sampleBody);

            if (doc.lastAutoTable) {
                currentY = doc.lastAutoTable.finalY + 10;
            }
        }


        // Reservoir Properties
        doc.setFontSize(16);
        doc.setFont(reportConstants.eMprintCondensedBold);
        doc.text(`${wellInformation.well} ${reportConstants.reservoirProperties}`, margin, currentY);
        currentY += 5;

        if (reportType === reportConstants.reportTypeMDT) {
            const reservoirHead = reportConstants.reservoirHeadPDF;
            const reservoirBody = reservoirProperties.map(val => [
                val.sampleId ?? reportConstants.dash,
                val.sampleDepth ?? reportConstants.dash,
                this._reportGeneralUtilityService.FormatToFourDecimals(val.reservoirTemperature) ?? reportConstants.dash,
                this._reportGeneralUtilityService.FormatToFourDecimals(val.reservoirPressure) ?? reportConstants.dash,
                this._reportGeneralUtilityService.FormatToFourDecimals(val.viscosity) ?? reportConstants.dash,
                this._reportGeneralUtilityService.FormatToFourDecimals(val.density) ?? reportConstants.dash,
                this._reportGeneralUtilityService.FormatToFourDecimals(val.formationVolumeFactor) ?? reportConstants.dash,
                this._reportGeneralUtilityService.FormatToFourDecimals(val.contaminatedGOR) ?? reportConstants.dash,
                this._reportGeneralUtilityService.FormatToFourDecimals(val.uncontaminatedGOR) ?? reportConstants.dash,
                this._reportGeneralUtilityService.FormatToFourDecimals(val.contaminantInLiveFluid) ?? reportConstants.dash,
                this._reportGeneralUtilityService.FormatToFourDecimals(val.contaminantInResidualFluid) ?? reportConstants.dash
            ]);
            this.GenerateAutoTable(doc, margin, currentY, reservoirHead, reservoirBody);
            if (doc.lastAutoTable) {
                currentY = doc.lastAutoTable.finalY + 10;
            }
        }
        else if (reportType === reportConstants.reportTypeDST) {
            const reservoirHead = reportConstants.reservoirHeadPDFExtended;
            const reservoirBody = reservoirPropertiesDST.map(val => [
                val.sampleId ?? reportConstants.dash,
                val.sampleDepth ?? reportConstants.dash,
                this._reportGeneralUtilityService.FormatToFourDecimals(val.reservoirTemperature) ?? reportConstants.dash,
                this._reportGeneralUtilityService.FormatToFourDecimals(val.reservoirPressure) ?? reportConstants.dash,
                this._reportGeneralUtilityService.FormatToFourDecimals(val.viscosity) ?? reportConstants.dash,
                this._reportGeneralUtilityService.FormatToFourDecimals(val.density) ?? reportConstants.dash,
                this._reportGeneralUtilityService.FormatToFourDecimals(val.formationVolumeFactor) ?? reportConstants.dash,
                this._reportGeneralUtilityService.FormatToFourDecimals(val.gor) ?? reportConstants.dash
            ]);
            this.GenerateAutoTable(doc, margin, currentY, reservoirHead, reservoirBody);
            if (doc.lastAutoTable) {
                currentY = doc.lastAutoTable.finalY + 10;
            }
        }
        else if (reportType === reportConstants.reportTypeSeparatorTest) {
            const reservoirHead = reportConstants.reservoirHeadPDFExtendedMinimal;
            const reservoirBody = reservoirPropertiesSeparator.map(val => [
                val.sampleId ?? reportConstants.dash,
                val.sampleDepth ?? reportConstants.dash,
                this._reportGeneralUtilityService.FormatToFourDecimals(val.reservoirTemperature) ?? reportConstants.dash,
                this._reportGeneralUtilityService.FormatToFourDecimals(val.reservoirPressure) ?? reportConstants.dash,
                this._reportGeneralUtilityService.FormatToFourDecimals(val.viscosity) ?? reportConstants.dash,
                this._reportGeneralUtilityService.FormatToFourDecimals(val.density) ?? reportConstants.dash
            ]);
            this.GenerateAutoTable(doc, margin, currentY, reservoirHead, reservoirBody);
            if (doc.lastAutoTable) {
                currentY = doc.lastAutoTable.finalY + 10;
            }
        }
        // Saturated Properties
        doc.setFontSize(16);
        doc.setFont(reportConstants.eMprintCondensedBold);
        doc.text(`${wellInformation.well} ${reportConstants.saturatedProperties}`, margin, currentY);
        currentY += 5;

        const saturatedHead = reportConstants.saturatedHeadPDF;
        const saturatedBody = saturatedProperties.map(val => [
            val.sampleId ?? reportConstants.dash,
            val.sampleDepth ?? reportConstants.dash,
            this._reportGeneralUtilityService.FormatToFourDecimals(val.saturationTemperature) ?? reportConstants.dash,
            this._reportGeneralUtilityService.FormatToFourDecimals(val.saturationPressure) ?? reportConstants.dash,
            this._reportGeneralUtilityService.FormatToFourDecimals(val.viscosityAtSaturationPressure) ?? reportConstants.dash,
            this._reportGeneralUtilityService.FormatToFourDecimals(val.densityAtSaturationPressure) ?? reportConstants.dash
        ]);
        this.GenerateAutoTable(doc, margin, currentY, saturatedHead, saturatedBody, doc.internal.pageSize.getWidth() * 0.5);

        doc.setFontSize(10);
        doc.setFont(reportConstants.eMprintCondensedRegular);
        currentY += 80;
        if (reportType === reportConstants.reportTypeDST) {
            doc.text(reportConstants.depthNote, margin, currentY)
        }
    }
    CompositionalAnalysis(doc: jsPDF, marginSize: number, bcaExp: BCAReports, wellName: string, sampleName: string) {
        this.reportPDFCompositionRenderService.CompositionalAnalysis(doc, marginSize, bcaExp, wellName, sampleName);
    }
    ParaffinPage(doc: jsPDF, marginSize: number, paraffinExp: ParaffinReports, appendix = "", wellNameVersion: string, sampleNameVersion: string) {
        this.reportPDFParaffinRenderService.ParaffinPage(doc, marginSize, paraffinExp, appendix, wellNameVersion, sampleNameVersion);
    }

    GenerateViscosityPage(doc: jsPDF, marginSize: number, viscosityReport: viscosityReports, appendix = "", wellNameVersion: string, sampleNameVersion: string) {
        this.reportPDFViscosityRenderService.GenerateViscosityPage(doc, marginSize, viscosityReport, appendix, wellNameVersion, sampleNameVersion);
    }

    SeparatorTest(doc: jsPDF, marginSize: number, msstExp: MSSTReportData, appendix = "", wellNameVersion: string, sampleNameVersion: string) {
        this.reportPDFMsstRenderService.SeparatorTest(doc, marginSize, msstExp, appendix, wellNameVersion, sampleNameVersion);
    }
    SeparatorTestAppendix(doc: jsPDF, marginSize: number, msstExp: MSSTReportData) {
        this.reportPDFMsstRenderService.SeparatorTestAppendix(doc, marginSize, msstExp);
    }
    CCE(doc: jsPDF, marginSize: number, CCEExp: CCEReports, wellName: string, sampleName: string) {
        this.reportPDFCceRenderService.CCE(doc, marginSize, CCEExp, wellName, sampleName);
    }
    CCEDewPoint(doc: jsPDF, marginSize: number, CCEDewPointExp: CCEDewPointReports, wellName: string, sampleName: string) {
        this.reportPDFCceRenderService.CCEDewPoint(doc, marginSize, CCEDewPointExp, wellName, sampleName);
    }
    CCESepGas(doc: jsPDF, marginSize: number, CCEExp: CCEReports, wellName: string, molPercentage = 0) {
        this.reportPDFCceRenderService.CCESepGas(doc, marginSize, CCEExp, wellName, molPercentage);
    }
    CCEDewPointSepGas(doc: jsPDF, marginSize: number, CCEExp: CCEDewPointReports, wellName: string, molPercentage = 0) {
        this.reportPDFCceRenderService.CCEDewPointSepGas(doc, marginSize, CCEExp, wellName, molPercentage);
    }

    FractionAppendix(doc: jsPDF, marginSize: number, fractionExp: FractionReports) {
        this.reportPDFFractionRenderService.FractionAppendix(doc, marginSize, fractionExp);
    }



    // Separator Gas Pdf
    GenerateSeparatorGasPdfReport(doc: jsPDF, marginSize: number, separatorData: SeparatorGasReports) {
        this.reportPDFSeparatorGasRenderService.GenerateSeparatorGasPdfReport(doc, marginSize, separatorData);
    }

    GenerateSeparatorOilPdfReport(doc: jsPDF, marginSize: number, separatorData: SeparatorOilReports, wellName: string, sampleName: string) {
        this.reportPDFSeparatorOilRenderService.GenerateSeparatorOilPdfReport(doc, marginSize, separatorData, wellName, sampleName);
    }
    RecombinationQC(doc: jsPDF, marginSize: number, bcaExp: RecombinationQCReportData, wellName: string) {
        this.reportPDFRecombinationQcRenderService.RecombinationQC(doc, marginSize, bcaExp, wellName);
    }

    Decontamination(doc: jsPDF, marginSize: number, decontExp: DecontaminationReportData, wellName: string, sampleName: string) {
        this.reportPDFDecontaminationRenderService.Decontamination(doc, marginSize, decontExp, wellName, sampleName);
    }

    SwellingReport(doc: jsPDF, marginSize: number, swellingExp: SwellingFinalReport, wellName: string, appendixText = '') {
        this.reportPDFSwellingRenderService.SwellingReport(doc, marginSize, swellingExp, wellName, appendixText);
    }

    SwellingGasCompDataReport(doc: jsPDF, marginSize: number, swellingExp: SwellingFinalReport, wellName: string) {
        this.reportPDFSwellingRenderService.SwellingGasCompDataReport(doc, marginSize, swellingExp, wellName);
    }

    SwellingCalculatedCompBalData(doc: jsPDF, marginSize: number, swellingExp: SwellingFinalReport, wellName: string) {
        this.reportPDFSwellingRenderService.SwellingCalculatedCompBalData(doc, marginSize, swellingExp, wellName);
    }




    summaryofCCE(doc: jsPDF, marginSize: number, CCEExp: SummaryOfCCEReports, wellName: string, sampleName: string, appendixText = '') {
        this.reportPDFCceRenderService.SummaryofCCE(doc, marginSize, CCEExp, wellName, sampleName, appendixText);
    }
    /*Reusable Methods*/



    JustifyText(doc: jsPDF, text: string, x: number, y: number, maxWidth: number, lineHeight: number): number {
        const words = text.split(' ');
        let line = '';
        const lines: string[] = [];
        words.forEach(word => {
            const testLine = line + word + ' ';
            const testWidth = doc.getTextWidth(testLine);
            if (testWidth > maxWidth && line !== '') {
                lines.push(line.trim());
                line = word + ' ';
            } else {
                line = testLine;
            }
        });
        if (line) lines.push(line.trim());
        lines.forEach((lineText, i) => {
            const lineWords = lineText.split(' ');
            if (lineWords.length === 1 || i === lines.length - 1) {
                doc.text(lineText, x, y);
            } else {
                const totalWordWidth = lineWords.reduce((acc, word) => acc + doc.getTextWidth(word), 0);
                const spaceCount = lineWords.length - 1;
                const extraSpace = (maxWidth - totalWordWidth) / spaceCount;
                let wordX = x;

                lineWords.forEach((word) => {
                    doc.text(word, wordX, y);
                    wordX += doc.getTextWidth(word) + extraSpace;
                });
            }
            y += lineHeight;
        });

        return y;
    }
    AddCenteredSection(doc: jsPDF, content: string, subContent = "", fontSizeTitle = 30, fontSizeSubTitle = 16) {
        doc.addPage();

        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();

        const text = content;
        doc.setFontSize(fontSizeTitle);
        doc.setFont(reportConstants.eMprintCondensedBold);
        const textWidth = doc.getTextWidth(text);
        const x = (pageWidth - textWidth) / 2;
        const y = pageHeight / 2;

        doc.text(text, x, y);
        doc.setFont(reportConstants.eMprintCondensedRegular);
        doc.setFontSize(fontSizeSubTitle);
        const textSubWidth = doc.getTextWidth(subContent);
        const x1 = (pageWidth - textSubWidth) / 2;
        const y1 = y + 10;
        if (subContent !== "") {
            doc.text(subContent, x1, y1);
        }
    }
    centerText(doc: jsPDF, text: string, y: number) {
        const pageWidth = doc.internal.pageSize.getWidth();
        const textWidth = doc.getTextWidth(text);
        const x = (pageWidth - textWidth) / 2;
        doc.text(text, x, y);
    }

    GenerateAutoTable(
        doc: jsPDF,
        margin: number,
        startY: number,
        head: string[][],
        body: string[][],
        tableWidth?: number
    ) {
        autoTable(doc, {
            head,
            body,
            startY,
            theme: reportConstants.grid,
            margin,
            styles: { fontSize: 8, lineWidth: 0, minCellHeight: 1 },
            headStyles: {
                fillColor: [192, 192, 192],
                halign: reportConstants.center,
                textColor: [0, 0, 0],
                fontStyle: reportConstants.normal,
                font: reportConstants.eMprintCondensedRegular,
                cellPadding: { top: 0.5, bottom: 0.5 }
            },
            bodyStyles: {
                halign: reportConstants.center,
                fontSize: 8,
                textColor: [0, 0, 0],
                font: reportConstants.eMprintCondensedRegular
            },
            tableWidth: tableWidth ?? reportConstants.auto
        });
    }

    LeftAlignText(doc: jsPDF, text: string, x: number, y: number, maxWidth: number, lineHeight: number): number {
        const words = text.split(' ');
        let line = '';
        const lines: string[] = [];

        words.forEach(word => {
            const testLine = line + word + ' ';
            const testWidth = doc.getTextWidth(testLine);
            if (testWidth > maxWidth && line !== '') {
                lines.push(line.trim());
                line = word + ' ';
            } else {
                line = testLine;
            }
        });

        if (line) lines.push(line.trim());

        lines.forEach(lineText => {
            doc.text(lineText, x, y);
            y += lineHeight;
        });

        return y;
    }


    CVDStage(doc: jsPDF, marginSize: number, cvdExp: CVDStageResult, appendix = "", wellNameVersion: string, sampleNameVersion: string) {
        this.reportPDFCvdRenderService.CVDStage(doc, marginSize, cvdExp, appendix, wellNameVersion, sampleNameVersion);
    }
    CVDOtherPages(doc: jsPDF, marginSize: number, cvdExp: CVDReport, wellNameVersion: string, sampleNameVersion: string) {
        this.reportPDFCvdRenderService.CVDOtherPages(doc, marginSize, cvdExp, wellNameVersion, sampleNameVersion);
    }

}
declare module 'jspdf' {
    interface jsPDF {
        lastAutoTable?: {
            finalY: number;
        };
    }
}


